'use client';

import { memo, useMemo } from 'react';
import { renderResumeHtml } from '@/lib/resume/templates/renderer';
import type { ResumeContent, ResumeSettings } from '@/lib/resume/types';
import { cn } from '@/lib/utils';

const MM_TO_PX = 3.7795;
const A4_WIDTH_PX = 210 * MM_TO_PX;
const A4_HEIGHT_PX = 297 * MM_TO_PX;
// Cards render at a fixed 430px-wide "sheet" (~26% wider than the original
// 340px, so the hero stack fills more of the page). Exported so
// ResumeCarousel can size its stage to match — the iframe below is scaled
// down by a fixed factor, not a percentage, so the two must agree on the
// same target width.
export const CARD_WIDTH_PX = 430;
const SCALE = CARD_WIDTH_PX / A4_WIDTH_PX;

interface ResumeBuilderPreviewCardProps {
  content: ResumeContent;
  settings: ResumeSettings;
  className?: string;
}

/**
 * A single resume "sheet" for the resume-builder hero illustration.
 *
 * Renders a sample resume through the real `renderResumeHtml()` the builder
 * itself uses, then scales the full A4 page into a small card with CSS
 * `transform: scale()` — the same "real document" thumbnail technique
 * ResumeShowcase uses for dashboard previews (full page in an iframe, scaled
 * down). It takes content/settings as props rather than a hardcoded sample so
 * ResumeCarousel can cycle it through several different specimens; it makes
 * no API calls and needs no session either way.
 *
 * Sizing is intentionally left to the parent: this component fills whatever
 * box it's given (`w-full h-full` via `className`) at a fixed 210:297
 * aspect ratio, so a caller like ResumeCarousel can position/scale/rotate the
 * outer box per card without this component knowing it's part of a stack.
 *
 * Memoized because ResumeCarousel re-renders on every shuffle tick — without
 * this, every visible card would redundantly recompile its Handlebars
 * template each tick even when its own content/settings hadn't changed.
 */
function ResumeBuilderPreviewCardImpl({ content, settings, className }: ResumeBuilderPreviewCardProps) {
  const previewHtml = useMemo(
    () => renderResumeHtml(content, settings).replace(/@font-face\s*\{[^{}]*\}/g, ''),
    [content, settings]
  );

  return (
    <div
      className={cn(
        'animate-fade-in relative overflow-hidden rounded-sm border border-border bg-white shadow-[0_1px_2px_rgba(27,27,27,.06),0_24px_48px_-24px_rgba(27,27,27,.35)]',
        className
      )}
      style={{ aspectRatio: '210 / 297' }}
    >
      <iframe
        srcDoc={previewHtml}
        title="Sample resume preview"
        tabIndex={-1}
        aria-hidden="true"
        scrolling="no"
        className="pointer-events-none absolute left-0 top-0 origin-top-left border-0 bg-white"
        style={{ width: A4_WIDTH_PX, height: A4_HEIGHT_PX, transform: `scale(${SCALE})` }}
      />
    </div>
  );
}

export const ResumeBuilderPreviewCard = memo(ResumeBuilderPreviewCardImpl);
